import { useEffect, useRef, useState } from 'react';

import { SegmentGroup } from '@chakra-ui/react';

import { IconsNocowanie } from '@nocowanie/icons';

import { RangePickerFlexDatesProps } from './range-picker-flex-dates.props';

import { RANGE_PICKER } from '../../../consts';
import {
    generateFlexDateOptions,
    getInitialFlexDaysValue,
    handleFlexDateSelection,
    setLastUserSelection,
    shouldPreserveUserChoice,
    shouldSyncWithExternal,
} from '../../../helpers';
import { ScrollableWrapper } from '../scrollable-wrapper';

export const RangePickerFlexDates = ({
    translationData,
    onFlexDateSelect,
    selectedFlexDays,
    instanceId = 'default',
    withDrawer = false,
}: RangePickerFlexDatesProps) => {
    const [internalSelectedFlexDays, setInternalSelectedFlexDays] = useState<number>(() =>
        getInitialFlexDaysValue(selectedFlexDays, instanceId),
    );

    const isUserChangeRef = useRef(false);

    useEffect(() => {
        if (shouldSyncWithExternal(selectedFlexDays, internalSelectedFlexDays)) {
            setInternalSelectedFlexDays(selectedFlexDays as number);

            if ((selectedFlexDays as number) > 0) {
                setLastUserSelection(selectedFlexDays as number, instanceId);
            }
        } else if (withDrawer && selectedFlexDays !== internalSelectedFlexDays) {
            setInternalSelectedFlexDays(selectedFlexDays as number);
        } else if (
            !withDrawer &&
            shouldPreserveUserChoice(selectedFlexDays, internalSelectedFlexDays)
        ) {
            // Preserve user's choice when external tries to reset
            onFlexDateSelect?.(internalSelectedFlexDays);
        }
    }, [selectedFlexDays, internalSelectedFlexDays, onFlexDateSelect, instanceId, withDrawer]);

    useEffect(() => {
        isUserChangeRef.current = false;
    });

    const flexDateOptions = generateFlexDateOptions(translationData);

    const handleFlexDateClick = (days: number) => {
        handleFlexDateSelection(
            days,
            {
                setInternalSelectedFlexDays,
                setIsUserChange: (isUserChange: boolean) => {
                    isUserChangeRef.current = isUserChange;
                },
                onFlexDateSelect,
            },
            instanceId,
        );
    };

    const scrollableStyles = {
        ml: 0,
        pt: 2.5,
        pb: 0.5,
        px: 6,
    };

    const renderSegmentGroup = () => (
        <SegmentGroup.Root
            variant={'separated'}
            value={internalSelectedFlexDays.toString()}
            onValueChange={details => {
                const days = details.value
                    ? parseInt(details.value)
                    : RANGE_PICKER.FLEX_DATES.DEFAULT;
                handleFlexDateClick(days);
            }}
            size={'sm'}
            data-testid={'flex-dates'}
        >
            <SegmentGroup.Indicator />
            {flexDateOptions.map(({ label, days }) => (
                <SegmentGroup.Item
                    key={days}
                    value={days.toString()}
                    data-testid={`flex-dates-option-${days}`}
                >
                    <SegmentGroup.ItemHiddenInput />
                    <SegmentGroup.ItemText>
                        {days > 0 && <IconsNocowanie.PlusMinus scale={0.6} ml={-1} />}
                        {label}
                    </SegmentGroup.ItemText>
                </SegmentGroup.Item>
            ))}
        </SegmentGroup.Root>
    );

    return withDrawer ? (
        <ScrollableWrapper scrollableContainerProps={scrollableStyles}>
            {renderSegmentGroup()}
        </ScrollableWrapper>
    ) : (
        renderSegmentGroup()
    );
};
